<i18n>
{
  "en": {
    "networks": {
      "id": "ID",
      "type": "Type",
      "broadcastAddress": "Broadcast",
      "netmaskAddress": "Netmask",
      "netmaskMask": "Mask",
      "startAddress": "IP From",
      "endAddress": "IP To",
      "startPort": "Port From",
      "endPort": "Port To",
      "agentUrl": "Agent URL",
      "title": "Network ranges",
      "location": "Location",
      "create": {
        "hint": "Add new network range"
      }
    }
  },
  "ru": {
    "networks": {
      "id": "ID",
      "type": "Тип",
      "broadcastAddress": "Широковещательный IP",
      "netmaskAddress": "Адрес",
      "netmaskMask": "Маска",
      "startAddress": "IP Начальный",
      "endAddress": "IP Конечный",
      "startPort": "Начальный порт",
      "endPort": "Конечный порт",
      "agentUrl": "URL агента",
      "title": "Сетевой диапазон",
      "location": "Локация",
      "create": {
        "hint": "Зарегистрировать новую подсеть"
      }
    }
  }
}
</i18n>

<template>
  <div class="content-block" id="networks">
    <div class="app-buttons">
      <dx-button type="normal"
        :text="$t('general.resetFilter')"
        styling-mode="contained"
        @click="onResetState"
      />

      <dx-button
        id="create"
        icon="plus"
        type="normal"
        :text="$t('general.create')"
        :hint="$t('networks.create.hint')"
        @click="onCreate"
      />
    </div>

    <dx-data-grid
      class="data-grid dx-card"
      :data-source="items"
      :remote-operations="remoteOperations"
      :syncLookupFilterValues="false"
      :height="gridHeight"
      @row-click="onRowClick"
      @content-ready="onContentReady"
      @context-menu-preparing="addMenuItems"
      @editor-preparing="onEditorPreparing"
      @initialized=initializedGrid
      ref="dataGrid"
    >
      <dx-paging :enabled="true" />
      <dx-pager
        :visible="true"
        :allowed-page-sizes="pageSize"
        display-mode="full"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="false"
      />
      <dx-scrolling mode="standard" />
      <dx-filter-panel :visible="showFilterPanel" />
      <dx-filter-row :visible="true" />
      <dx-sorting mode="multiple" />
      <dx-group-panel :visible="false" :allowColumnDragging="false" />
      <dx-grouping :auto-expand-all="true" />
      <dx-state-storing
        :enabled="true"
        type="custom"
        :customLoad="stateLoad"
        :customSave="stateSave"
      />

      <dx-column
        :group-index="0"
        sort-order="desc"
        data-field="location.name"
        :caption="$t('networks.location')"
        :width="160"
        :visible-index="0"
      />
      <dx-column
        data-field="agent.url"
        :caption="$t('networks.agentUrl')"
        :visible-index="1"
      />
      <dx-column
        data-field="rangeType"
        :caption="$t('networks.type')"
        :width="120"
        :visible-index="2"
      />
      <dx-column
        data-field="community"
        caption="Community name"
        :width="140"
        :visible="false"
        :visible-index="3"
      />
      <dx-column
        data-field="broadcastAddress"
        :caption="$t('networks.broadcastAddress')"
        :width="120"
        :visible-index="4"
      />
      <dx-column
        data-field="netmaskAddress"
        :caption="$t('networks.netmaskAddress')"
        :width="120"
        :visible-index="5"
      />
      <dx-column
        data-field="netmaskMask"
        :caption="$t('networks.netmaskMask')"
        :width="120"
        :visible-index="6"
      />
      <dx-column
        data-field="startAddress"
        :caption="$t('networks.startAddress')"
        :width="120"
        :visible-index="7"
      />
      <dx-column
        data-field="endAddress"
        :caption="$t('networks.endAddress')"
        :width="120"
        :visible-index="8"
      />
      <dx-column
        data-field="startPort"
        :caption="$t('networks.startPort')"
        :width="80"
        :visible-index="9"
      />
      <dx-column
        data-field="endPort"
        :caption="$t('networks.endPort')"
        :width="80"
        :visible-index="10"
      />
    </dx-data-grid>

    <CustomColumnChooser
      :visible="visible"
      :columns="columns"
      :onApply="onApply"
      :onHiding="onHiding"
    />
  </div>
</template>

<script>
import baseView from "../base";
import appInfo from "../../app-info";

import DxDataGrid, {
  DxColumn, DxFilterRow, DxPaging, DxStateStoring, DxPager, DxSorting, DxGroupPanel, DxGrouping, DxFilterPanel, DxScrolling
} from "devextreme-vue/data-grid";
import { DxButton } from 'devextreme-vue';

import CustomColumnChooser from "@/components/column-chooser/CustomColumnChooser.vue";

import { findNetwork } from "@/services/api";
import { AclHandler } from "@/services/acl-handler";

import { DataDrid } from "@/mixins/dataDridMixin";
import { DataLocation } from "@/mixins/dataLocationMixin";

import {
  PAGE_HEADER_HEIGHT,
  PAGE_PADDING,
  PAGE_TOOLBARS_HEIGHT,
  PAGE_FOOTER_HEIGHT,
  GRID_HEADER_HEIGHT,
  GRID_FILTER_ROW_HEIGHT,
  GRID_FILTER_PANEL_HEIGHT,
  GRID_PAGER_HEIGHT
} from '@/shared/consts/layout';

export default {
  extends: baseView,
  mixins: [DataDrid, DataLocation],
  components: {
    DxDataGrid, DxColumn, DxFilterRow, DxPaging, DxButton, DxStateStoring, CustomColumnChooser,
    DxPager, DxSorting, DxGroupPanel, DxGrouping, DxFilterPanel, DxScrolling
  },
  data() {
    return {
      isCloudMode: this.$store.getters.isCloudMode,
      remoteOperations: { groupPaging: true },
      items: [],
    };
  },
  computed: {
    appUser() {
      return this.$store.getters.user;
    },
    aclUser() {
      return new AclHandler(this.appUser, this.isCloudMode);
    },
    showFilterPanel() {
      return this.aclUser.hasGrantForOperation('BTN_CLEAR_FILTER', 'EXECUTE') || false;
    },
    sumGap() {
      return PAGE_HEADER_HEIGHT + PAGE_PADDING + PAGE_TOOLBARS_HEIGHT + PAGE_FOOTER_HEIGHT;
    },
    gridHeight() {
      const appHeightSize = this.$store.getters.appContainerSize.height;
      return (appHeightSize || 800) - this.sumGap;
    },
    gridBodyHeight() {
      return this.gridHeight - GRID_HEADER_HEIGHT - GRID_FILTER_ROW_HEIGHT - (this.showFilterPanel ? GRID_FILTER_PANEL_HEIGHT : 0) - GRID_PAGER_HEIGHT;
    },
  },
  methods: {
    onRowClick(e) {
      if (window.getSelection().focusOffset !== window.getSelection().anchorOffset || !e.data.id)
        return;

      if (e.event.metaKey || e.event.ctrlKey) {
        const routeData = this.$router.resolve({
          name: "network",
          params: { id: e.data.id }
        });
        window.open(routeData.href, "_blank");
      } else {
        this.$router.push({
          name: "network",
          params: { id: e.data.id }
        });
      }
    },
    onEditorPreparing(e) {
      this.applyFilterRowTimeout(e);
    },
    onContentReady(e) {
      this.addFitToPageHeightButton(e);
    },
    onCreate() {
      this.$router.push({ name: "network", params: { id: -1 } });
    },
    onResetState() {
      this.dataGrid.clearFilter();
    },
  },
  async created() {
    this.gridStateKey = 'gridNetworks';

    document.title = `${appInfo.title} - ${this.$t('networks.title')}`;
    this.$store.commit("setBreadcrumb", [{
      link: "/networks",
      title: this.$t('networks.title')
    }]);

    try {
      this.items = await findNetwork();
    } catch (error) {
      this.onDataError(error);
    }
  },
  beforeDestroy() {
    const state = this.dataGrid.state();
    this.stateSave(state);
  },
  watch: { }
};
</script>

<style lang="scss">
#networks {
  .dx-toolbar.dx-widget.dx-visibility-change-handler.dx-collection {
    padding: 10px 10px 0;
  }
}
</style>
